import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

export type ButtonVariant = 'primary' | 'strong' | 'outline' | 'quiet' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

const VARIANT: Record<ButtonVariant, string> = {
    primary: 'bg-accent text-on-accent hover:bg-accent-hover',
    strong: 'bg-inverse text-on-inverse hover:bg-inverse-hover',
    outline: 'bg-surface text-strong shadow-control hover:bg-sunken',
    quiet: 'bg-transparent text-strong hover:bg-hover',
    danger: 'bg-danger-solid text-on-danger hover:bg-danger-solid-hover',
    link: 'h-auto min-h-0 rounded-none px-0 text-link underline underline-offset-2 hover:decoration-2',
};

const SIZE: Record<ButtonSize, string> = {
    sm: 'h-9 px-3 text-caption font-semibold after:absolute after:-inset-x-0 after:-inset-y-1 after:content-[""]',
    md: 'h-control px-4 text-small font-semibold',
    lg: 'h-cta px-6 text-body font-semibold',
    xl: 'h-cta-lg px-6 text-body font-semibold',
};

const SQUARE: Record<ButtonSize, string> = { sm: 'size-9 after:absolute after:-inset-1 after:content-[""]', md: 'size-11', lg: 'size-[3.25rem]', xl: 'size-14' };

interface StyleOptions {
    variant?: ButtonVariant;
    size?: ButtonSize;
    iconOnly?: boolean;
    fullWidth?: boolean;
}

/** Class list shared by Button, LinkButton and IconButton. */
export const buttonClass = ({ variant = 'strong', size = 'md', iconOnly = false, fullWidth = false }: StyleOptions) =>
    clsx(
        'relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-button',
        'transition-[color,background-color,transform] duration-hover ease-snap active:scale-[0.97] active:duration-press',
        'disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100 aria-disabled:cursor-not-allowed aria-disabled:opacity-60',
        VARIANT[variant],
        variant !== 'link' && (iconOnly ? SQUARE[size] : SIZE[size]),
        iconOnly && 'rounded-control p-0',
        fullWidth && 'w-full'
    );

const Spinner = () => (
    <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
);

interface ContentProps extends StyleOptions {
    icon?: ReactNode;
    children?: ReactNode;
}

export interface ButtonProps extends ContentProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    loading?: boolean;
}

/** `loading` keeps the label (no width jump), swaps the icon for a spinner and disables the button. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    { variant, size, iconOnly, fullWidth, loading = false, icon, children, className, type = 'button', disabled, ...rest },
    ref
) {
    const { t } = useTranslation();
    return (
        <button
            ref={ref}
            type={type}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            className={clsx(buttonClass({ variant, size, iconOnly, fullWidth }), className)}
            {...rest}
        >
            {loading ? <Spinner /> : icon}
            {children}
            {loading && <span className="sr-only">{t('ui.loading')}</span>}
        </button>
    );
});

export interface LinkButtonProps extends ContentProps, Omit<LinkProps, 'children'> {}

export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(function LinkButton(
    { variant, size, iconOnly, fullWidth, icon, children, className, ...rest },
    ref
) {
    return (
        <Link ref={ref} className={clsx(buttonClass({ variant, size, iconOnly, fullWidth }), className)} {...rest}>
            {icon}
            {children}
        </Link>
    );
});
